import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const GOALS = ["5K", "10K", "Half Marathon", "Marathon", "HYROX", "Ironman", "Trail / Ultra", "Just getting started"];

export const JoinForm = ({ compact = false, source = "site" }) => {
  const [form, setForm] = useState({ email: "", city: "", pace: "", goal: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    try {
      const res = await api.post("/newsletter", { ...form, source });
      toast.success(res.data.message || "You are in.");
      setDone(true);
    } catch {
      toast.error("Something broke. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="join-form-success" className="border border-ink p-8 lg:p-10 text-ink">
        <p className="font-mono-label text-ink/40">WELCOME TO THE CLUB</p>
        <p className="font-display text-3xl lg:text-4xl mt-3">YOU ARE IN.</p>
        <p className="mt-3 opacity-70 text-sm max-w-sm">
          A welcome email is on its way. Watch your inbox for the next run near you, and bring somebody with you.
        </p>
        <div className="grid gap-2 mt-8">
          {[["/clubs", "FIND YOUR CREW", "The full national directory, human-verified"], ["/ranked", "SEE THE 2026 RANKINGS", "The best run clubs in America, scored"], ["/tools", "DO THE MATH", "21 free training calculators"]].map(([to, label, sub]) => (
            <Link key={to} to={to} data-testid={`join-success-cta-${to.slice(1)}`} className="group flex items-center justify-between border border-ink/20 px-5 py-4 hover:bg-ink hover:text-paper transition-colors duration-300">
              <span>
                <span className="font-mono-label block">{label}</span>
                <span className="text-xs opacity-50">{sub}</span>
              </span>
              <ArrowUpRight size={16} className="opacity-40 group-hover:opacity-100" />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full bg-transparent border-b border-ink/30 py-3 text-sm text-ink placeholder:text-ink/40 focus:border-ink transition-colors duration-200";

  return (
    <form onSubmit={submit} data-testid="join-form" className="space-y-5 text-ink">
      <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <input
        data-testid="join-email-input"
        type="email"
        required
        placeholder="EMAIL ADDRESS"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className={inputCls}
      />
      {!compact && (
        <input
          data-testid="join-city-input"
          type="text"
          placeholder="YOUR CITY"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          className={inputCls}
        />
      )}
      {!compact && (
        <details className="pt-1" data-testid="join-optional-details">
          <summary className="font-mono-label text-ink/50 cursor-pointer list-none hover:text-ink transition-colors duration-200">
            + OPTIONAL: PACE & GOAL (SHARPENS WHAT WE SEND)
          </summary>
          <div className="mt-4 space-y-4">
            <input
              data-testid="join-pace-input"
              type="text"
              placeholder="TYPICAL PACE (E.G. 9:30/MI)"
              value={form.pace}
              onChange={(e) => setForm({ ...form, pace: e.target.value })}
              className={inputCls}
            />
            <div className="flex flex-wrap gap-2">
              {GOALS.map((g) => (
                <button
                  type="button"
                  key={g}
                  data-testid={`join-goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setForm({ ...form, goal: g })}
                  className={`font-mono-label px-3 py-2 border transition-colors duration-200 ${
                    form.goal === g ? "bg-ink text-paper border-ink" : "border-ink/30 text-ink hover:border-ink"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </details>
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid="join-submit-button"
        className="btn-invert font-mono-label bg-ink text-paper border border-ink px-8 py-4 w-full sm:w-auto hover:bg-transparent hover:text-ink disabled:opacity-50"
      >
        {loading ? "JOINING..." : "JOIN THE MOVEMENT"}
      </button>
    </form>
  );
};
